import { createSelector, createSlice, SliceCaseReducers } from '@reduxjs/toolkit';

export interface INLPFound {
  industries?: INLPDataType[];
  skills?: INLPDataType[];
  passions?: INLPDataType[];
  clients?: INLPDataType[];
  projects?: INLPDataType[];
  people?: INLPPerson[];
  account_holders?: boolean;
}

export interface INLPDataType {
  name: string;
  uuid: string;
  original_name: string;
  type?: string;
  aliases: string[];
}

export interface INLPPerson {
  name: string;
  uuid: string;
  slug: string;
  role_name?: string;
  level_name?: string;
  company_name: string;
  company_slug: string;
  avatar?: string;
  city?: string;
  linkedin: any;
  slack: any;
  email?: string;
  connection_count: number;
  cross_kyu_teaming: boolean;
  is_freelancer: boolean;
}

export interface INLPState {
  found: INLPFound;
  notFound: string;
  prompt: string;
  prompt_uuid: string;
  feedback: string;
  showSearchbar: boolean;
  isLoading: boolean;
  showNoResults: boolean;
  showNLPError: boolean;
}

const initialState = {
  showSearchbar: true,
  found: null,
  notFound: null,
  prompt: null,
  prompt_uuid: null,
  feedback: null,
  isLoading: false,
  showNoResults: false,
  showNLPError: false,
};

export const slice = createSlice<INLPState, SliceCaseReducers<INLPState>>({
  name: 'nlp',
  initialState,
  reducers: {
    setShowSearchbar: (state, action) => {
      state.showSearchbar = action.payload;
    },
    setFound: (state, action) => {
      state.found = action.payload;
    },
    setNotFound: (state, action) => {
      state.notFound = action.payload;
    },
    setPrompt: (state, action) => {
      state.prompt = action.payload;
    },
    setPromptUuid: (state, action) => {
      state.prompt_uuid = action.payload;
    },
    setFeedback: (state, action) => {
      state.feedback = action.payload;
    },
    setIsNLPLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setShowNoResults: (state, action) => {
      state.showNoResults = action.payload;
    },
    setShowNLPError: (state, action) => {
      state.showNLPError = action.payload;
    },
  },
});

export const {
  setShowSearchbar,
  setFound,
  setNotFound,
  setPrompt,
  setPromptUuid,
  setFeedback,
  setIsNLPLoading,
  setShowNoResults,
  setShowNLPError,
} = slice.actions;

export const showSearchbarSelector = (state) => state.nlp.showSearchbar;
export const foundSelector = (state): INLPFound => state.nlp.found;
export const notFoundSelector = (state) => state.nlp.notFound;
export const promptSelector = (state) => state.nlp.prompt;
export const promptUuidSelector = (state) => state.nlp.prompt_uuid;
export const feedbackSelector = (state) => state.nlp.feedback;
export const isNLPLoadingSelector = (state) => state.nlp.isLoading;
export const showNoResultsSelector = (state) => state.nlp.showNoResults;
export const showNLPErrorSelector = (state) => state.nlp.showNLPError;
export const unrecognizedKeywordsSelector = createSelector(
  [notFoundSelector],
  (notFound) => notFound?.unrecognized || []
);

export const allGroupsSelector = createSelector([foundSelector], (found) => {
  if (!found) return [];

  const allDatatypes = Object.keys(found)
    .filter((key) => key !== 'account_holders')
    .reduce((acc, key) => {
      return [...acc, ...(found[key] || [])];
    }, []);

  const allGroups = allDatatypes.reduce((acc, datatype) => {
    acc.push(datatype.grouping || datatype.groups_matched[0]);
    return acc;
  }, []);

  const uniqueGroups = allGroups.filter((group, index, self) => self.indexOf(group) === index);

  return uniqueGroups;
});

export const groupingsSelector = createSelector([foundSelector], (found) => {
  if (!found) return [];

  const grouped = Object.keys(found)
    .filter((key) => key !== 'account_holders')
    .reduce((acc, key) => {
      const group: any[] = found[key];
      group.forEach((item) => {
        if (item.grouping) {
          acc[item.grouping] = acc[item.grouping] || [];
          acc[item.grouping].push({ ...item, datatype: key });
        }
      });
      return acc;
    }, {});

  return Object.values(grouped)
    .map((group: any, index) => {
      const groupName = Object.keys(grouped)[index];
      return group.map((item: any) => ({
        uuid: item.uuid,
        type: item.datatype,
        group_name: groupName,
      }));
    })
    .reduce((acc, group) => {
      acc.push(...group);
      return acc;
    }, []);
});

export const groupingsSelectorWithPeople = createSelector([foundSelector], (found) => {
  if (!found) return [];

  const grouped = Object.keys(found)
    .filter((key) => key !== 'account_holders')
    .reduce((acc, key) => {
      const group: any[] = found[key];
      group.forEach((item) => {
        if (item.grouping) {
          acc[item.grouping] = acc[item.grouping] || [];
          acc[item.grouping].push({ ...item, datatype: key });
        }
        if (key === 'people') {
          acc[item.groups_matched[0]] = acc[item.groups_matched[0]] || [];
          acc[item.groups_matched[0]].push({ ...item, datatype: 'contributors' });
        }
      });
      return acc;
    }, {});

  return Object.values(grouped)
    .map((group: any, index) => {
      const groupName = Object.keys(grouped)[index];
      return group.map((item: any) => ({
        uuid: item.uuid,
        type: item.datatype,
        group_name: groupName,
      }));
    })
    .reduce((acc, group) => {
      acc.push(...group);
      return acc;
    }, []);
});

export default slice.reducer;
